import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ClipboardList, Plus, Search, ChevronRight, Upload, Download, Trash2, FileDown } from 'lucide-react';
import { toast } from 'sonner';

const API = '';
const STAGES = [
  { key: 'cutting', name: 'Cutting' },
  { key: 'preparation', name: 'Preparation' },
  { key: 'closing', name: 'Closing' },
  { key: 'lasting', name: 'Lasting' },
  { key: 'pasting', name: 'Pasting' },
  { key: 'finishing', name: 'Finishing' },
  { key: 'packing', name: 'Packing' },
  { key: 'dispatch', name: 'Dispatch' },
];

export default function JobCards() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [jobCards, setJobCards] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pos, setPOs] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [filterPO, setFilterPO] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPendingStage, setFilterPendingStage] = useState('');
  const [filterSKU, setFilterSKU] = useState('');
  const [skuInput, setSkuInput] = useState('');
  const [createModal, setCreateModal] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [cuttingWorkerId, setCuttingWorkerId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detailCard, setDetailCard] = useState(null);
  const [jcBalance, setJcBalance] = useState({}); // {"sku|||size": {ordered, issued, balance}}

  const fetchJobCards = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: '20' });
      if (filterPO) params.append('po_number', filterPO);
      if (filterStatus) params.append('status', filterStatus);
      if (filterPendingStage) params.append('pending_stage', filterPendingStage);
      if (filterSKU) params.append('sku', filterSKU);
      const { data } = await axios.get(`${API}/api/job-cards?${params}`, { withCredentials: true });
      setJobCards(data.job_cards || []);
      setTotal(data.total || 0);
    } catch {}
  }, [page, filterPO, filterStatus, filterPendingStage, filterSKU]);

  // Debounce SKU input → triggers fetch via filterSKU change
  useEffect(() => {
    const t = setTimeout(() => { setFilterSKU(skuInput.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [skuInput]);

  const buildExportUrl = () => {
    const qs = new URLSearchParams();
    if (filterPO) qs.set('po_number', filterPO);
    if (filterStatus) qs.set('status', filterStatus);
    if (filterPendingStage) qs.set('pending_stage', filterPendingStage);
    if (filterSKU) qs.set('sku', filterSKU);
    const q = qs.toString();
    return `/api/job-cards/export${q ? `?${q}` : ''}`;
  };

  const fetchPOs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/po?limit=500`, { withCredentials: true });
      setPOs(data.pos || []);
    } catch {}
  }, []);

  const fetchWorkers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/job-workers`, { withCredentials: true });
      setWorkers(data);
    } catch {}
  }, []);

  useEffect(() => { fetchJobCards(); }, [fetchJobCards]);
  useEffect(() => { fetchPOs(); fetchWorkers(); }, [fetchPOs, fetchWorkers]);

  const handlePOSelect = async (poNum) => {
    const po = pos.find(p => p.po_number === poNum);
    setSelectedPO(po);
    setLineItems(po?.line_items || []);
    setSelectedItems([]);
    setJcBalance({});
    if (po?.po_number) {
      try {
        const { data } = await axios.get(`${API}/api/po/${encodeURIComponent(po.po_number)}/jc-balance`, { withCredentials: true });
        const map = {};
        (data.items || []).forEach(it => { map[`${it.sku}|||${it.size}`] = it; });
        setJcBalance(map);
      } catch { setJcBalance({}); }
    }
  };

  const toggleLineItem = (li) => {
    const key = `${li.sku}|||${li.size}`;
    const balRow = jcBalance[key];
    const bal = balRow ? balRow.balance_qty : (li.ordered_qty || 0);
    if (bal <= 0) return; // can't select if nothing left to issue
    setSelectedItems(prev => {
      const exists = prev.find(i => `${i.sku}|||${i.size}` === key);
      if (exists) return prev.filter(i => `${i.sku}|||${i.size}` !== key);
      return [...prev, { sku: li.sku, size: String(li.size), brand: li.brand || '', color: '', planned_qty: bal }];
    });
  };

  const updateSelectedItem = (key, field, value) => {
    setSelectedItems(prev => prev.map(i => `${i.sku}|||${i.size}` === key ? { ...i, [field]: value } : i));
  };

  const createJobCard = async () => {
    setError('');
    if (!selectedPO || selectedItems.length === 0) {
      setError('Select a PO and at least one line item');
      return;
    }
    if (!cuttingWorkerId) {
      setError('Select a cutting worker');
      return;
    }
    const items = selectedItems.map(i => ({
      sku: i.sku, size: i.size, brand: i.brand, color: i.color,
      planned_qty: parseInt(i.planned_qty) || 0
    }));
    if (items.some(i => i.planned_qty <= 0)) {
      setError('All items must have planned qty > 0');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/job-cards`, {
        po_number: selectedPO.po_number,
        cutting_worker_id: cuttingWorkerId,
        line_items: items
      }, { withCredentials: true });
      toast.success(`${data.job_card_number} created with ${items.length} items`);
      setCreateModal(false);
      setSelectedItems([]);
      setCuttingWorkerId('');
      // Refresh balance for the chosen PO so next JC sees correct numbers
      if (selectedPO?.po_number) {
        try {
          const { data: bd } = await axios.get(`${API}/api/po/${encodeURIComponent(selectedPO.po_number)}/jc-balance`, { withCredentials: true });
          const map = {};
          (bd.items || []).forEach(it => { map[`${it.sku}|||${it.size}`] = it; });
          setJcBalance(map);
        } catch {}
      }
      fetchJobCards();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create Job Card');
    } finally { setLoading(false); }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/api/job-cards/template`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', 'job_card_template.xlsx'); document.body.appendChild(link);
      link.click(); link.remove();
    } catch { toast.error('Failed to download template'); }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedPO) { toast.error('Select a PO first'); return; }
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(`${API}/api/job-cards/upload?po_number=${encodeURIComponent(selectedPO.po_number)}`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(data.message);
      if (data.warnings) toast.warning(`Warnings: ${data.warnings.join(', ')}`);
      setCreateModal(false);
      fetchJobCards();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    e.target.value = '';
  };

  const viewDetail = async (jcNumber) => {
    try {
      const { data } = await axios.get(`${API}/api/job-cards/${jcNumber}`, { withCredentials: true });
      setDetailCard(data);
    } catch { toast.error('Failed to load job card details'); }
  };

  const downloadPDF = async (jcNumber) => {
    try {
      const response = await axios.get(`${API}/api/job-cards/${jcNumber}/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${jcNumber}.pdf`);
      document.body.appendChild(link);
      link.click(); link.remove();
    } catch { toast.error('Failed to download PDF'); }
  };

  const deleteJobCard = async (jcNumber) => {
    if (!isAdmin) return;
    const confirmed = window.confirm(
      `Delete Job Card ${jcNumber}?\n\nThis is permanent and cannot be undone. Any production entries on this JC will block deletion.`
    );
    if (!confirmed) return;
    try {
      await axios.delete(`${API}/api/job-cards/${jcNumber}`, { withCredentials: true });
      toast.success(`Job Card ${jcNumber} deleted`);
      fetchJobCards();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to delete Job Card';
      toast.error(msg);
    }
  };

  const totalPages = Math.ceil(total / 20);

  return (
    <div className="p-6" data-testid="job-cards-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tighter text-[#09090B]">Job Cards</h1>
          <p className="text-sm text-zinc-500">Create and manage production job cards (format: RPI-DDMMYY-POXXXX-XXXX)</p>
        </div>
        <Button className="h-9 rounded-sm bg-[#09090B]" onClick={() => setCreateModal(true)} data-testid="create-job-card-btn">
          <Plus size={14} className="mr-1.5" /> New Job Card
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-zinc-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Filter by PO</Label>
            <Select value={filterPO || 'all'} onValueChange={v => { setFilterPO(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="mt-1 h-9 rounded-sm" data-testid="filter-po-select">
                <SelectValue placeholder="All POs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All POs</SelectItem>
                {pos.map(po => (
                  <SelectItem key={po.id} value={po.po_number}><span className="font-mono">{po.po_number}</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Status</Label>
            <Select value={filterStatus || 'all'} onValueChange={v => { setFilterStatus(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="mt-1 h-9 rounded-sm" data-testid="filter-status-select">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Pending at Stage</Label>
            <Select value={filterPendingStage || 'all'} onValueChange={v => { setFilterPendingStage(v === 'all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="mt-1 h-9 rounded-sm" data-testid="filter-pending-stage-select">
                <SelectValue placeholder="Any stage" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any stage</SelectItem>
                <SelectItem value="cutting">Cutting</SelectItem>
                <SelectItem value="prep_printing">Printing</SelectItem>
                <SelectItem value="prep_skiving">Skiving</SelectItem>
                <SelectItem value="prep_marking">Marking</SelectItem>
                <SelectItem value="prep_crimping">Crimping</SelectItem>
                <SelectItem value="prep_nailing">Nailing</SelectItem>
                <SelectItem value="prep_socks">Socks</SelectItem>
                <SelectItem value="closing">Closing</SelectItem>
                <SelectItem value="lasting">Lasting</SelectItem>
                <SelectItem value="pasting">Pasting</SelectItem>
                <SelectItem value="finishing">Finishing</SelectItem>
                <SelectItem value="packing">Packing</SelectItem>
                <SelectItem value="dispatch">Dispatch</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">SKU Search</Label>
            <div className="relative mt-1">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                value={skuInput}
                onChange={(e) => setSkuInput(e.target.value)}
                placeholder="e.g. EP1642BROWN"
                className="h-9 pl-7 rounded-sm font-mono text-xs"
                data-testid="filter-sku-input"
              />
            </div>
          </div>
          <div className="flex items-end justify-between gap-2">
            <span className="text-sm text-zinc-500">{total} job card{total !== 1 ? 's' : ''}</span>
            <a
              href={buildExportUrl()}
              download
              className="inline-flex items-center justify-center h-9 px-3 rounded-sm text-xs border border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-900 no-underline"
              data-testid="export-job-cards-btn"
            >
              <Download size={12} className="mr-1" /> Download
            </a>
          </div>
        </div>
      </div>

      {/* Job Cards Table */}
      <div className="bg-white border border-zinc-200">
        <div className="p-4 border-b border-zinc-200 flex items-center gap-2">
          <ClipboardList size={18} className="text-zinc-500" />
          <h2 className="font-heading font-bold text-lg">Job Cards Register</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="job-cards-table">
            <thead className="bg-zinc-50">
              <tr>
                <th className="text-left p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Job Card # / SKUs</th>
                <th className="text-left p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">PO Number</th>
                <th className="text-center p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Items</th>
                <th className="text-right p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Total Planned</th>
                <th className="text-left p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Issued By</th>
                <th className="text-left p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Date</th>
                <th className="text-center p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Current Stage</th>
                <th className="text-left p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Current Worker</th>
                <th className="text-center p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Status</th>
                <th className="text-right p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobCards.map((jc, idx) => {
                const totalPlanned = (jc.line_items || []).reduce((s, li) => s + (li.planned_qty || 0), 0);
                const itemCount = (jc.line_items || []).length;
                // Unique SKUs without size suffix (strip trailing _<digits>)
                const skuList = Array.from(new Set((jc.line_items || []).map(li => (li.sku || '').replace(/_\d+$/, '')))).sort();
                const isStuck = !!jc.stuck;
                const isCompleted = jc.status === 'completed' || jc.current_stage === 'Completed';
                return (
                  <tr
                    key={jc.job_card_number}
                    className={`border-t border-zinc-200 ${isStuck ? 'bg-red-50' : (idx % 2 === 1 ? 'bg-zinc-50/30' : '')}`}
                    data-testid={`jc-row-${jc.job_card_number}`}
                  >
                    <td className="p-3 align-top">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-xs text-[#09090B]">{jc.job_card_number}</span>
                        {isStuck && (
                          <span
                            className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm bg-red-600 text-white tracking-wide"
                            title={`Stuck for ${jc.stuck_days} days in ${jc.current_stage}`}
                            data-testid={`jc-stuck-${jc.job_card_number}`}
                          >
                            STUCK {jc.stuck_days}d
                          </span>
                        )}
                      </div>
                      {skuList.length > 0 && (
                        <div className="mt-1 text-[10px] font-mono text-zinc-500" data-testid={`jc-skus-${jc.job_card_number}`}>
                          {skuList.join(', ')}
                        </div>
                      )}
                    </td>
                    <td className="p-3 font-mono text-xs align-top">{jc.po_number}</td>
                    <td className="p-3 text-center font-mono text-xs align-top">{itemCount}</td>
                    <td className="p-3 text-right font-mono text-xs font-bold align-top">{totalPlanned}</td>
                    <td className="p-3 text-xs align-top">{jc.issued_by}</td>
                    <td className="p-3 text-xs text-zinc-500 align-top">{jc.date_of_issue?.split('T')[0]}</td>
                    <td className="p-3 text-center align-top">
                      <span
                        className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                          jc.current_stage === 'Completed' ? 'bg-emerald-100 text-emerald-700'
                          : jc.current_stage === 'Cutting' ? 'bg-amber-50 text-amber-700'
                          : 'bg-blue-50 text-blue-700'
                        }`}
                        data-testid={`jc-current-stage-${jc.job_card_number}`}
                      >
                        {jc.current_stage || 'Cutting'}
                      </span>
                    </td>
                    <td className="p-3 text-xs align-top" data-testid={`jc-current-worker-${jc.job_card_number}`}>
                      {jc.current_worker_name || <span className="text-zinc-400">—</span>}
                    </td>
                    <td className="p-3 text-center align-top">
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full capitalize ${
                        isCompleted ? 'bg-emerald-100 text-emerald-700'
                        : jc.status === 'open' ? 'bg-blue-50 text-blue-700'
                        : 'bg-zinc-100 text-zinc-600'
                      }`}>
                        {isCompleted ? 'completed' : (jc.status || 'open')}
                      </span>
                    </td>
                    <td className="p-3 text-right align-top">
                      <div className="flex justify-end gap-1">
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => downloadPDF(jc.job_card_number)} data-testid={`pdf-jc-${jc.job_card_number}`}>
                          <FileDown size={12} className="mr-1" /> PDF
                        </Button>
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => viewDetail(jc.job_card_number)} data-testid={`view-jc-${jc.job_card_number}`}>
                          <Search size={12} className="mr-1" /> View
                        </Button>
                        {isAdmin && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                            onClick={() => deleteJobCard(jc.job_card_number)}
                            data-testid={`delete-jc-${jc.job_card_number}`}
                            title="Delete Job Card (admin only)"
                          >
                            <Trash2 size={12} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {jobCards.length === 0 && (
                <tr><td colSpan={10} className="p-8 text-center text-zinc-500">No job cards found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="p-3 border-t border-zinc-200 flex items-center justify-between">
            <span className="text-xs text-zinc-500">Page {page} of {totalPages}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="h-7 text-xs" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
              <Button variant="outline" size="sm" className="h-7 text-xs" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      <Dialog open={createModal} onOpenChange={setCreateModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="create-job-card-dialog">
          <DialogHeader>
            <DialogTitle>Create New Job Card</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm">{error}</div>}

            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Select PO *</Label>
                <Select value={selectedPO?.po_number || 'none'} onValueChange={v => handlePOSelect(v === 'none' ? '' : v)}>
                  <SelectTrigger className="mt-1 h-9 rounded-sm" data-testid="jc-po-select">
                    <SelectValue placeholder="Choose PO" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Choose PO</SelectItem>
                    {pos.map(po => (
                      <SelectItem key={po.id} value={po.po_number}>
                        <span className="font-mono">{po.po_number}</span> - {po.buyer_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" size="sm" className="h-9 text-xs" onClick={downloadTemplate} data-testid="download-jc-template">
                <Download size={12} className="mr-1" /> Template
              </Button>
              <label className="cursor-pointer">
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleBulkUpload} data-testid="upload-jc-file" />
                <div className="flex items-center gap-1 h-9 px-3 text-xs border border-zinc-200 rounded-sm hover:bg-zinc-50">
                  <Upload size={12} /> Upload
                </div>
              </label>
            </div>

            {/* Line Items Selection */}
            {lineItems.length > 0 && (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">Select SKUs (click to toggle)</Label>
                <div className="border border-zinc-200 rounded-sm max-h-48 overflow-y-auto">
                  {lineItems.map(li => {
                    const key = `${li.sku}|||${li.size}`;
                    const isSelected = selectedItems.some(i => `${i.sku}|||${i.size}` === key);
                    const balRow = jcBalance[key];
                    const ordered = balRow ? balRow.ordered_qty : (li.ordered_qty || 0);
                    const issued = balRow ? balRow.issued_qty : 0;
                    const balance = balRow ? balRow.balance_qty : ordered;
                    const exhausted = balance <= 0;
                    return (
                      <div key={key} onClick={() => !exhausted && toggleLineItem(li)}
                        className={`flex items-center justify-between p-2 text-xs border-b border-zinc-100 last:border-0 transition-colors ${exhausted ? 'bg-zinc-50 opacity-60 cursor-not-allowed' : (isSelected ? 'bg-emerald-50 cursor-pointer' : 'hover:bg-zinc-50 cursor-pointer')}`}
                        data-testid={`jc-item-${li.sku}-${li.size}`}>
                        <div>
                          <span className="font-mono font-medium">{li.sku}</span>
                          <span className="text-zinc-400 ml-2">Size {li.size}</span>
                          {li.brand && <span className="text-zinc-400 ml-2">| {li.brand}</span>}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-mono text-zinc-500" title="Ordered">Ord: {ordered}</span>
                          <span className="font-mono text-amber-700" title="Issued to existing JCs">Iss: {issued}</span>
                          <span className={`font-mono font-bold ${exhausted ? 'text-red-500' : 'text-emerald-700'}`} title="Available balance for new JC">Bal: {balance}</span>
                          {isSelected && <span className="text-emerald-600 font-bold">&#10003;</span>}
                          {exhausted && <span className="text-[10px] text-red-500 font-semibold">FULL</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Selected Items — editable qty and color */}
            {selectedItems.length > 0 && (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 block">Selected Items ({selectedItems.length})</Label>
                <div className="space-y-2">
                  {selectedItems.map(item => {
                    const key = `${item.sku}|||${item.size}`;
                    const balRow = jcBalance[key];
                    const maxQty = balRow ? balRow.balance_qty : undefined;
                    const exceeded = maxQty !== undefined && parseInt(item.planned_qty) > maxQty;
                    return (
                      <div key={key} className={`flex items-center gap-2 p-2 rounded-sm text-xs ${exceeded ? 'bg-red-50 ring-1 ring-red-200' : 'bg-zinc-50'}`}>
                        <span className="font-mono font-medium flex-1">
                          {item.sku} / Size {item.size}
                          {maxQty !== undefined && <span className="ml-2 text-[10px] text-zinc-500">(Max: {maxQty})</span>}
                        </span>
                        <Input value={item.color} onChange={e => updateSelectedItem(key, 'color', e.target.value)} placeholder="Color" className="h-7 w-24 text-xs rounded-sm" />
                        <Input type="number" max={maxQty} value={item.planned_qty} onChange={e => updateSelectedItem(key, 'planned_qty', e.target.value)} className={`h-7 w-20 text-xs rounded-sm font-mono ${exceeded ? 'border-red-400 text-red-700' : ''}`} data-testid={`jc-qty-${item.sku}-${item.size}`} />
                        <button onClick={() => setSelectedItems(prev => prev.filter(i => `${i.sku}|||${i.size}` !== key))} className="text-red-400 hover:text-red-600">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Cutting Worker Selection */}
            {selectedItems.length > 0 && (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Assign to Cutting Worker *</Label>
                <Select value={cuttingWorkerId || 'none'} onValueChange={v => setCuttingWorkerId(v === 'none' ? '' : v)}>
                  <SelectTrigger className="mt-1 h-9 rounded-sm" data-testid="jc-cutting-worker">
                    <SelectValue placeholder="Select cutting worker" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select cutting worker</SelectItem>
                    {workers.map(w => (
                      <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2 border-t border-zinc-200">
              <Button variant="outline" className="h-9 rounded-sm" onClick={() => setCreateModal(false)}>Cancel</Button>
              <Button className="h-9 rounded-sm bg-[#09090B]" onClick={createJobCard} disabled={loading} data-testid="submit-job-card">
                {loading ? 'Creating...' : `Create Job Card (${selectedItems.length} items)`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={!!detailCard} onOpenChange={() => setDetailCard(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="job-card-detail-dialog">
          {detailCard && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ClipboardList size={18} /> {detailCard.job_card_number}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                  <div><span className="text-zinc-500">PO:</span> <span className="font-mono ml-1">{detailCard.po_number}</span></div>
                  <div><span className="text-zinc-500">Items:</span> <span className="font-mono ml-1">{detailCard.line_items?.length || 0}</span></div>
                  <div><span className="text-zinc-500">Total Planned:</span> <span className="font-mono font-bold ml-1">{(detailCard.line_items || []).reduce((s, li) => s + li.planned_qty, 0)}</span></div>
                  <div><span className="text-zinc-500">Issued By:</span> <span className="ml-1">{detailCard.issued_by}</span></div>
                  <div><span className="text-zinc-500">Date:</span> <span className="ml-1">{detailCard.date_of_issue?.split('T')[0]}</span></div>
                  <div><span className="text-zinc-500">Status:</span> <span className="ml-1">{detailCard.status}</span></div>
                  <div><span className="text-zinc-500">Cutting Worker:</span> <span className="ml-1" data-testid="detail-cutting-worker">{detailCard.cutting_worker_name || '-'}</span></div>
                </div>

                {/* Line Items */}
                <div>
                  <h3 className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2">Line Items</h3>
                  <table className="w-full text-xs">
                    <thead className="bg-zinc-50">
                      <tr>
                        <th className="text-left p-2">SKU</th>
                        <th className="text-center p-2">Size</th>
                        <th className="text-left p-2">Brand</th>
                        <th className="text-left p-2">Color</th>
                        <th className="text-right p-2">Planned Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detailCard.line_items || []).map(li => (
                        <tr key={`${li.sku}-${li.size}`} className="border-t border-zinc-100">
                          <td className="p-2 font-mono">{li.sku}</td>
                          <td className="p-2 text-center font-mono">{li.size}</td>
                          <td className="p-2">{li.brand || '-'}</td>
                          <td className="p-2">{li.color || '-'}</td>
                          <td className="p-2 text-right font-mono font-bold">{li.planned_qty}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Production Entries */}
                {detailCard.production_entries?.length > 0 && (
                  <div>
                    <h3 className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2">Production Entries</h3>
                    <table className="w-full text-xs">
                      <thead className="bg-zinc-50">
                        <tr>
                          <th className="text-left p-2">SKU</th>
                          <th className="text-left p-2">Stage</th>
                          <th className="text-right p-2">In</th>
                          <th className="text-right p-2">Out</th>
                          <th className="text-right p-2">Loss</th>
                          <th className="text-left p-2">Worker</th>
                          <th className="text-right p-2">Wages</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailCard.production_entries.map((e, i) => (
                          <tr key={i} className="border-t border-zinc-100">
                            <td className="p-2 font-mono">{e.sku} / {e.size}</td>
                            <td className="p-2">{e.stage_name}</td>
                            <td className="p-2 text-right font-mono">{e.input_qty}</td>
                            <td className="p-2 text-right font-mono">{e.output_qty}</td>
                            <td className="p-2 text-right font-mono text-red-600">{e.loss_qty > 0 ? `-${e.loss_qty}` : '0'}</td>
                            <td className="p-2">{e.job_worker_name}</td>
                            <td className="p-2 text-right font-mono">{e.wages}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
